import axios from "axios";
import Cookies from "universal-cookie";
import SessionManager from "./SessionManager";
import moment from "moment";

const cookies = new Cookies();

const APEX_DOMAIN = "ifillip.com";
const DEFAULT_HOST = "ifillip.com";
// const DEFAULT_HOST = "vivekananda.ifillip.com";
// const DEFAULT_HOST = "test.ifillip.com";
// const DEFAULT_HOST = "dev.ifillip.com";
const TENANT_HEADER = "Tenant-ID";

/* ────────────────────────────────────────────────────────────────
   Tenant / host resolution

   Priority:
   1. Subdomain the user typed at login (stored as the "tenant"
      cookie) → {subdomain}.ifillip.com. This is the override.
   2. Otherwise mirror whatever real tenant host the browser is on
      (e.g. abc.ifillip.com).
   3. Localhost, bare IPs, apex domain, "www", or nothing typed →
      DEFAULT_HOST, using its own first label as the tenant header.

   The subdomain field is OPTIONAL: leave it blank and the app uses
   the default/host resolution below.
──────────────────────────────────────────────────────────────── */
const extractSubdomain = (host) => host.split(".")[0];

const getStoredSubdomain = () => {
  const value = cookies.get("tenant");
  return value ? String(value).trim().toLowerCase() : "";
};

const resolveTenant = () => {
  // 1. Typed subdomain overrides everything → prefix onto the apex
  const typed = getStoredSubdomain();
  if (typed) {
    return { host: `${typed}.${APEX_DOMAIN}`, subdomain: typed };
  }

  if (typeof window === "undefined" || !window.location?.hostname) {
    return { host: DEFAULT_HOST, subdomain: extractSubdomain(DEFAULT_HOST) };
  }

  const { hostname } = window.location;
  const isIp = /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname);
  const parts = hostname.split(".");

  if (
    hostname === "localhost" ||
    isIp ||
    parts.length < 3 ||
    parts[0] === "www"
  ) {
    return { host: DEFAULT_HOST, subdomain: extractSubdomain(DEFAULT_HOST) };
  }

  return { host: hostname, subdomain: parts[0] };
};

const { host: RESOLVED_HOST, subdomain: TENANT_SUBDOMAIN } = resolveTenant();

// Image URL Base — derived from the resolved tenant host
export const IMAGE_BASE_URL = `https://${RESOLVED_HOST}/api/`;

const API_BASE_URL =
  process.env.REACT_APP_API_URL || `https://${RESOLVED_HOST}/api/V1/api`;

// ADMISSION LOGOS
export const admissionLogo1 = `${IMAGE_BASE_URL}school_logo/profile.jpg`;
export const admissionLogo2 = `${IMAGE_BASE_URL}school_logo/1755065163.png`;

/* ============================================================
   COOKIE OPTIONS
   ============================================================ */

const COOKIE_OPTIONS = {
  path: "/",
  maxAge: 86400,
  secure: process.env.NODE_ENV === "production",
  sameSite: "strict",
};

/* ============================================================
   TOKEN SERVICE
   ============================================================ */

export const TokenService = {
  getToken: () => cookies.get("token"),

  setToken: (token) => {
    cookies.set("token", token, COOKIE_OPTIONS);
  },

  removeToken: () => {
    cookies.remove("token", { path: "/" });
  },

  getUser: () => {
    const user = cookies.get("user");
    return user && user.role ? user : null;
  },

  setUser: (user) => {
    cookies.set("user", user, COOKIE_OPTIONS);
  },

  removeUser: () => {
    cookies.remove("user", { path: "/" });
  },

  clearAll: () => {
    cookies.remove("token", { path: "/" });
    cookies.remove("user", { path: "/" });
  },
};

/* ============================================================
   TENANT SERVICE
   Stores the OPTIONAL subdomain the user typed. When absent the
   app falls back to host/default resolution above.
   ============================================================ */

export const TenantService = {
  getTenant: () => getStoredSubdomain() || null,

  setTenant: (subdomain) => {
    const value = (subdomain || "").trim().toLowerCase();
    if (value) {
      cookies.set("tenant", value, COOKIE_OPTIONS);
    } else {
      // Blank field → clear any old tenant so default resolution applies
      cookies.remove("tenant", { path: "/" });
    }
  },

  removeTenant: () => {
    cookies.remove("tenant", { path: "/" });
  },
};

/* ============================================================
   AXIOS INSTANCE
   ============================================================ */

export const AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
});

/* ============================================================
   REQUEST INTERCEPTOR
   ============================================================ */

AxiosInstance.interceptors.request.use(
  (config) => {
    const sub = getStoredSubdomain();

    // Only rewrite host when a subdomain was actually typed/stored
    if (sub && !process.env.REACT_APP_API_URL) {
      config.baseURL = `https://${sub}.${APEX_DOMAIN}/api/V1/api`;
    }

    // 🏫 Only send Tenant-ID when a subdomain exists — no fallback
    if (sub) {
      config.headers[TENANT_HEADER] = sub;
    }

    const token = TokenService.getToken();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

/* ============================================================
   RESPONSE INTERCEPTOR
   ============================================================ */

AxiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (originalRequest?.url?.includes("/login/login")) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const token = TokenService.getToken();

      if (token) {
        try {
          SessionManager.showSessionExpiredModal(originalRequest).catch(
            (modalErr) => {
              console.warn(
                "SessionManager modal promise rejected (handled):",
                modalErr,
              );
            },
          );

          return new Promise(() => {});
        } catch (modalError) {
          console.error("Session modal error:", modalError);

          TokenService.clearAll();
          window.location.href = "/login";

          return Promise.reject(error);
        }
      } else {
        TokenService.clearAll();
        window.location.href = "/login";

        return Promise.reject(error);
      }
    }

    if (error.response?.status === 401 && originalRequest._retry) {
      TokenService.clearAll();
      SessionManager.handleSessionInvalid();
      window.location.href = "/login";
    }

    return Promise.reject(error);
  },
);

/* ============================================================
   AUTH API
   ============================================================ */

export const authAPI = {
  login: (credentials) =>
    AxiosInstance.post("/login/login", {
      email: credentials.email,
      password: credentials.password,
    }),

  getProfile: () => AxiosInstance.get("/login/profile"),

  refreshToken: () => {
    const token = TokenService.getToken();
    const user = TokenService.getUser();
    const sub = getStoredSubdomain();

    const base =
      process.env.REACT_APP_API_URL ||
      (sub
        ? `https://${sub}.${APEX_DOMAIN}/api/V1/api`
        : `https://${RESOLVED_HOST}/api/V1/api`);

    const payload = {
      user_id: user?.id || null,
      email: user?.email || null,
      role: user?.role?.id || null,
    };

    return axios.post(`${base}/login/refresh-token`, payload, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        // 🏫 Only when typed
        ...(sub ? { [TENANT_HEADER]: sub } : {}),
      },
    });
  },

  logout: () => AxiosInstance.post("/login/logout"),
};

/* ============================================================
   DATE HELPERS
   ============================================================ */

export const toUtcIso = (date) =>
  date
    ? moment(date).startOf("day").utc().format("YYYY-MM-DD[T]HH:mm:ss.SSS[Z]")
    : null;

export const toLocalDate = (utcDate) =>
  utcDate ? moment.utc(utcDate).local().format("YYYY-MM-DD") : null;

/* ============================================================
   TAX RATES
   ============================================================ */

export const CGST_RATE = 9;
export const SGST_RATE = 9;

/* ============================================================
   RAZORPAY
   ============================================================ */

export const RAZORPAY_SCRIPT_SRC =
  "https://checkout.razorpay.com/v1/checkout.js";
// export const RAZORPAY_KEY_ID = "rzp_test_c0hHzXEKePlGCL";
export const RAZORPAY_KEY_ID = "rzp_live_TgDq55grZ2gpRp";

/* ============================================================
   POSTAL PINCODE API
   ============================================================ */

export const POSTAL_PINCODE_API = "https://api.postalpincode.in/pincode/";

export default AxiosInstance;
